import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Page,
  Header,
  Content,
  InfoCard,
  Table,
} from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Typography,
  Breadcrumbs,
  Link,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import FolderIcon from '@material-ui/icons/Folder';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import GetAppIcon from '@material-ui/icons/GetApp';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
  listS3Objects,
  getPresignedDownloadUrl,
  getBreadcrumbPath,
  formatFileSize,
  formatDate,
  type S3Object,
} from '../services/s3';

const BUCKET_NAME = 'bnf-datalake';
const BASE_PREFIX = 'prod/raw/allure/';

export const S3ReportsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const prefix = searchParams.get('prefix') || BASE_PREFIX;
  
  const [objects, setObjects] = useState<S3Object[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  
  const fetchObjects = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await listS3Objects(discoveryApi, fetchApi, prefix);
      
      // Separate folders and files, then sort
      const folders = response.objects
        .filter(obj => obj.isFolder)
        .sort((a, b) => a.name.localeCompare(b.name));
      
      const files = response.objects
        .filter(obj => !obj.isFolder)
        .sort((a, b) => a.name.localeCompare(b.name));
      
      setObjects([...folders, ...files]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load objects');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchObjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix]);
  
  const handleNavigate = (newPrefix: string) => {
    navigate(`/qa-automated-tests/s3-reports?prefix=${encodeURIComponent(newPrefix)}`);
  };
  
  const handleDownload = async (key: string) => {
    setDownloadingKey(key);
    
    try {
      const url = await getPresignedDownloadUrl(discoveryApi, fetchApi, key);
      
      if (url) {
        // Open presigned URL in new tab to trigger download
        window.open(url, '_blank');
      } else {
        setError('Failed to generate download URL');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloadingKey(null);
    }
  };
  
  const breadcrumbs = getBreadcrumbPath(prefix);
  
  return (
    <Page themeId="tool">
      <Header 
        title="S3 Allure Reports" 
        subtitle={`Bucket: ${BUCKET_NAME}`}
      >
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/qa-automated-tests')}
          style={{ marginRight: 8 }}
        >
          Back to QA Dashboard
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={fetchObjects}
          disabled={loading}
        >
          Refresh
        </Button>
      </Header>
      
      <Content>
        <InfoCard>
          <Box padding={2}>
            {/* Breadcrumb navigation */}
            <Box marginBottom={2}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Path:</strong>
              </Typography>
              <Breadcrumbs separator="/" aria-label="breadcrumb">
                <Link
                  color="inherit"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigate(BASE_PREFIX);
                  }}
                >
                  s3://{BUCKET_NAME}/
                </Link>
                {breadcrumbs.map((crumb, index) => (
                  <Link
                    key={crumb.path}
                    color={index === breadcrumbs.length - 1 ? 'textPrimary' : 'inherit'}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigate(crumb.path);
                    }}
                  >
                    {crumb.name}
                  </Link>
                ))}
              </Breadcrumbs>
            </Box>
            
            {/* Loading state */}
            {loading && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            )}
            
            {/* Error state */}
            {error && (
              <Alert severity="error" style={{ marginBottom: 16 }}>
                {error}
              </Alert>
            )}
            
            {/* Empty state */}
            {!loading && !error && objects.length === 0 && (
              <Box textAlign="center" padding={4}>
                <Typography variant="body1" color="textSecondary">
                  No objects found in this location.
                </Typography>
              </Box>
            )}
            
            {/* Objects table */}
            {!loading && !error && objects.length > 0 && (
              <Table
                options={{
                  padding: 'dense',
                  search: false,
                  paging: false,
                  sorting: false,
                }}
                data={objects.map(obj => ({
                  key: obj.key,
                  name: obj.name,
                  size: obj.size,
                  lastModified: obj.lastModified,
                  isFolder: obj.isFolder,
                }))}
                columns={[
                  {
                    title: 'Type',
                    field: 'isFolder',
                    width: '50px',
                    render: (row: any) => (
                      row.isFolder ? (
                        <FolderIcon style={{ color: '#ffa000' }} />
                      ) : (
                        <InsertDriveFileIcon style={{ color: '#757575' }} />
                      )
                    ),
                  },
                  {
                    title: 'Name',
                    field: 'name',
                    render: (row: any) => (
                      row.isFolder ? (
                        <Link
                          component="button"
                          onClick={() => handleNavigate(row.key)}
                          style={{ 
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                        >
                          {row.name}
                        </Link>
                      ) : (
                        <Typography variant="body2">
                          {row.name}
                        </Typography>
                      )
                    ),
                  },
                  {
                    title: 'Size',
                    field: 'size',
                    width: '100px',
                    render: (row: any) => (
                      <Typography variant="body2" color="textSecondary">
                        {formatFileSize(row.size)}
                      </Typography>
                    ),
                  },
                  {
                    title: 'Last Modified',
                    field: 'lastModified',
                    width: '200px',
                    render: (row: any) => (
                      <Typography variant="body2" color="textSecondary">
                        {formatDate(row.lastModified)}
                      </Typography>
                    ),
                  },
                  {
                    title: 'Actions',
                    field: 'key',
                    width: '100px',
                    render: (row: any) => (
                      !row.isFolder && (
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(row.key)}
                            disabled={downloadingKey === row.key}
                          >
                            {downloadingKey === row.key ? (
                              <CircularProgress size={20} />
                            ) : (
                              <GetAppIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      )
                    ),
                  },
                ]}
              />
            )}
          </Box>
        </InfoCard>
        
        {/* Help text */}
        <Box marginTop={2}>
          <InfoCard title="About S3 Allure Reports">
            <Typography variant="body2" color="textSecondary">
              Browse and download raw Allure reports stored in S3.
              <br /><br />
              <strong>Bucket:</strong> s3://{BUCKET_NAME}/
              <br />
              <strong>Base Path:</strong> {BASE_PREFIX}
              <br /><br />
              Click on folders to navigate. Click the download icon to download files.
              Download URLs are presigned and expire after 15 minutes.
            </Typography>
          </InfoCard>
        </Box>
      </Content>
    </Page>
  );
};
